import React, { useState, useEffect, useRef } from "react";
import {
  Upload,
  Download,
  Loader,
  AlertCircle,
  FileText,
  ArrowLeft,
  Users,
  Search,
  X,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  Sparkles,
  Info,
  Send,
  Phone,
} from "lucide-react";
import moment from "moment-timezone";
import WhatsAppPreview from "./WhatsAppNewPreview";
import { getTemplates } from "../apis/template";
import { getSegments } from "../apis/segments";
import { sendTestTemplate } from "../apis/campaign";
import { usePhoneAccount } from "../contexts/PhoneAccountContext";

const CreateCampaignForm = ({ onCancel, onSubmit }) => {
  const { selectedPhoneId, selectedPhoneAccount } = usePhoneAccount();
  const [templates, setTemplates] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [selectedTemplateForPreview, setSelectedTemplateForPreview] = useState(null);
  const [originalTemplate, setOriginalTemplate] = useState(null);
  const [useSegment, setUseSegment] = useState(false);
  const [templateValidationError, setTemplateValidationError] = useState("");
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [activePreviewIndex, setActivePreviewIndex] = useState(0);
  
  const carouselRef = useRef(null);

  const [campaignData, setCampaignData] = useState({
    name: "",
    description: "",
    templateIds: [],
    whatsapp_account_id: "",
    segment_id: "",
    use_segment: false,
    csvFile: null,
    csvData: [],
    sendOption: "now",
    scheduledDate: "",
    scheduledTime: "",
    scheduledTimestamp: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  const [errors, setErrors] = useState({});
  const [csvValidationErrors, setCsvValidationErrors] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [defaultValues, setDefaultValues] = useState({});
  
  // Test feature states
  const [showTestSection, setShowTestSection] = useState(false);
  const [testPhoneNumber, setTestPhoneNumber] = useState("");
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);


  useEffect(() => {
    if (selectedPhoneId) {
      loadTemplates(selectedPhoneId);
      loadSegments();
      handleInputChange("whatsapp_account_id", selectedPhoneId);
    }
  }, [selectedPhoneId]);

  // Deselect all templates when phone number selection changes
  useEffect(() => {
    if (selectedPhoneId) {
      setSelectedTemplates([]);
      setSelectedTemplateForPreview(null);
      setOriginalTemplate(null);
      setDefaultValues({});
      setTemplateValidationError("");
      handleInputChange("templateIds", []);
    }
  }, [selectedPhoneId, selectedPhoneAccount]);

  const loadTemplates = async (phoneId) => {
    if (!phoneId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("userAccessToken");
      const response = await getTemplates(token, { limit: 1000, offset: 0, phoneId: phoneId });

      const rawTemplates = response.data.metaTemplates || response.data.templates || [];
      const filtered = rawTemplates.filter(
        (t) => (t.status === "approved" || t.status === "APPROVED")
      );
      setTemplates(filtered);
    } catch (err) {
      console.error("Failed to load templates:", err);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSegments = async () => {
    try {
      const token = localStorage.getItem("userAccessToken");
      const response = await getSegments(token, { status: "active" });
      setSegments(response.data.segments || []);
    } catch (err) {
      console.error("Failed to load segments:", err);
    }
  };

  const handleInputChange = (field, value) => {
    setCampaignData((prev) => {
      const updated = { ...prev, [field]: value };
      
      if (field === "scheduledDate" || field === "scheduledTime") {
        const date = field === "scheduledDate" ? value : prev.scheduledDate;
        const time = field === "scheduledTime" ? value : prev.scheduledTime;
        
        if (date && time) {
          const timeWithSeconds = time.length === 5 ? `${time}:00` : time;
          updated.scheduledTimestamp = `${date} ${timeWithSeconds}`;
        } else {
          updated.scheduledTimestamp = "";
        }
      }
      
      return updated;
    });
    
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    
    if (field === "scheduledDate" || field === "scheduledTime") {
      if (errors.scheduledTimestamp) {
        setErrors((prev) => ({ ...prev, scheduledTimestamp: "" }));
      }
    }
  };

  const transformTemplateForPreview = (template) => {
    if (!template) return null;
    
    if (template.body && !template.template_json) {
      return {
        ...template,
        headerFormat: template.headerFormat || template.headerType || "NONE",
        buttons: template.buttons || [],
        variables: template.variables || [],
      };
    }
    
    let templateJson = template.template_json || {};
    if (typeof templateJson === 'string') {
      try {
        templateJson = JSON.parse(templateJson);
      } catch (e) {
        templateJson = {};
      }
    }
    
    const components = templateJson.components || [];
    
    if (components.length === 0) {
      return {
        ...template,
        body: template.body || '',
        footer: template.footer || '',
        headerFormat: template.headerFormat || template.headerType || "NONE",
        headerText: template.headerText || '',
        header_media_url: template.header_media_url || null,
        buttons: template.buttons || [],
        variables: template.variables || [],
      };
    }
    
    const bodyComponent = components.find(c => c.type === 'BODY');
    const body = bodyComponent?.text || template.body || '';
    
    const footerComponent = components.find(c => c.type === 'FOOTER');
    const footer = footerComponent?.text || template.footer || '';
    
    const headerComponent = components.find(c => c.type === 'HEADER');
    const headerFormat = headerComponent?.format?.toUpperCase() || template.headerFormat || template.headerType?.toUpperCase() || 'NONE';
    const headerText = headerComponent?.text || template.headerText || '';
    const headerMediaUrl = template.header_media_url || null;
    
    const buttonsComponent = components.find(c => c.type === 'BUTTONS');
    const rawButtons = buttonsComponent?.buttons || template.buttons || [];
    const buttons = rawButtons.map((btn, index) => ({
      id: index,
      type: btn.type,
      text: btn.text || btn.url || btn.phone_number || `Button ${index + 1}`,
      url: btn.url,
      phone_number: btn.phone_number,
    }));
    
    const variableMatches = body.match(/\{\{(\d+)\}\}/g) || [];
    const variables = variableMatches.map((match, index) => {
      const varNum = match.replace(/\{\{|\}\}/g, '');
      // body_text is an array of arrays, so we need body_text[0][varNum - 1]
      // varNum is a string, so we need to convert to number and subtract 1 for 0-based indexing
      const varIndex = parseInt(varNum, 10) - 1;
      const sampleValue = bodyComponent?.example?.body_text?.[0]?.[varIndex];
      return {
        id: index,
        name: varNum,
        sample: sampleValue || `Sample ${varNum}`,
      };
    });
    
    return {
      ...template,
      body,
      footer,
      headerFormat,
      headerText,
      header_media_url: headerMediaUrl,
      buttons,
      variables,
    };
  };

  const validateTemplateCompatibility = (selectedTemplateObjects) => {
    if (selectedTemplateObjects.length === 0) {
      return { valid: true, error: "" };
    }

    if (selectedTemplateObjects.length === 1) {
      return { valid: true, error: "" };
    }

    const templateHeaders = selectedTemplateObjects.map(template => {
      const headers = generateCSVHeaders(template);
      const sortedHeaders = [...headers].sort((a, b) => {
        if (a === 'phone_number') return -1;
        if (b === 'phone_number') return 1;
        return a.localeCompare(b);
      });
      return {
        templateId: template.id,
        templateName: template.name,
        headers: sortedHeaders,
        headersString: sortedHeaders.join(', '),
      };
    });

    const firstTemplateHeaders = templateHeaders[0].headers;

    const allMatch = templateHeaders.every(t => {
      if (t.headers.length !== firstTemplateHeaders.length) {
        return false;
      }
      return t.headers.every((header, index) => header === firstTemplateHeaders[index]);
    });

    if (!allMatch) {
      const headersStr = templateHeaders.map(t => 
        `"${t.templateName}": [${t.headersString}]`
      ).join('; ');
      
      return {
        valid: false,
        error: `Templates have different CSV requirements. Each template needs different columns: ${headersStr}. All templates must require the same CSV columns.`,
      };
    }

    return { valid: true, error: "" };
  };

  const handleTemplateToggle = (templateId) => {
    const normalizedTemplateId = String(templateId).trim();
    const currentSelected = [...selectedTemplates];
    
    const index = currentSelected.findIndex(id => String(id).trim() === normalizedTemplateId);
    
    let newSelected;
    if (index >= 0) {
      newSelected = currentSelected.filter(id => String(id).trim() !== normalizedTemplateId);
    } else {
      newSelected = [...currentSelected, normalizedTemplateId];
    }

    setSelectedTemplates(newSelected);
    handleInputChange("templateIds", newSelected);

    if (newSelected.length > 0) {
      const selectedTemplateObjects = newSelected
        .map(id => templates.find(t => String(t.id).trim() === String(id).trim()))
        .filter(Boolean);

      const validation = validateTemplateCompatibility(selectedTemplateObjects);
      setTemplateValidationError(validation.error);

      if (validation.valid && selectedTemplateObjects.length > 0) {
        const firstTemplate = selectedTemplateObjects[0];
        const transformedTemplate = transformTemplateForPreview(firstTemplate);
        transformedTemplate.selectedTemplates = selectedTemplateObjects.map(t => ({
          id: t.id,
          name: t.name,
          category: t.category,
        }));
        setSelectedTemplateForPreview(transformedTemplate);
        setOriginalTemplate(firstTemplate);

        const requiredParams = extractRequiredParameters(firstTemplate);
        const newDefaults = {};
        requiredParams.forEach(param => {
          newDefaults[param.key] = '';
        });
        setDefaultValues(newDefaults);
        setActivePreviewIndex(0);
      } else {
        setSelectedTemplateForPreview(null);
        setOriginalTemplate(null);
        setDefaultValues({});
      }
    } else {
      setSelectedTemplateForPreview(null);
      setOriginalTemplate(null);
      setDefaultValues({});
      setTemplateValidationError("");
    }
  };

  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim());
    return result;
  };

  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      setErrors((prev) => ({ ...prev, csvFile: "Please upload a CSV file" }));
      setCsvValidationErrors([]);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const lines = text.split("\n").filter((line) => line.trim());
        
        if (lines.length === 0) {
          setErrors((prev) => ({ ...prev, csvFile: "CSV file is empty" }));
          return;
        }

        const headers = parseCSVLine(lines[0]).map((h) => h.replace(/^"|"$/g, '').trim());
        
        const hasPhoneColumn = headers.some(h => 
          h.toLowerCase() === 'phone_number' || 
          h.toLowerCase() === 'phone' ||
          h.toLowerCase() === 'phone number'
        );

        if (!hasPhoneColumn) {
          setErrors((prev) => ({ 
            ...prev, 
            csvFile: "CSV must contain a 'phone_number' column" 
          }));
          return;
        }

        const data = lines.slice(1, 6).map((line) => {
          const values = parseCSVLine(line).map((v) => v.replace(/^"|"$/g, '').trim());
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || "";
          });
          return row;
        });

        setCampaignData((prev) => ({
          ...prev,
          csvFile: file,
          csvData: data,
        }));
        setErrors((prev) => ({ ...prev, csvFile: "" }));
        setCsvValidationErrors([]);
      } catch (error) {
        console.error("Error parsing CSV:", error);
        setErrors((prev) => ({ 
          ...prev, 
          csvFile: "Error parsing CSV file. Please check the format." 
        }));
      }
    };
    reader.readAsText(file);
  };

  const extractRequiredParameters = (template) => {
    if (!template) {
      return [];
    }

    const parameters = [];

    let templateJson = template.template_json || {};
    if (typeof templateJson === 'string') {
      try {
        templateJson = JSON.parse(templateJson);
      } catch (e) {
        templateJson = {};
      }
    }

    const components = templateJson.components || [];

    const headerComponent = components.find(c => c.type === 'HEADER');
    if (headerComponent) {
      const headerFormat = headerComponent.format?.toUpperCase() || template.headerFormat?.toUpperCase() || 'NONE';
      const headerText = headerComponent.text || template.headerText || '';
      
      if (headerFormat === 'TEXT') {
        const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
        headerVarMatches.forEach((match) => {
          const varNum = match.replace(/\{\{|\}\}/g, '');
          parameters.push({
            key: `header_${varNum}`,
            component: 'HEADER',
            varNum,
            placeholder: match,
            label: `Header Variable ${varNum}`,
            description: `Value for ${match} in header`,
          });
        });
      }
    } else if (template.headerFormat || template.headerType) {
      const headerFormat = (template.headerFormat || template.headerType).toUpperCase();
      const headerText = template.headerText || '';
      
      if (headerFormat === 'TEXT') {
        const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
        headerVarMatches.forEach((match) => {
          const varNum = match.replace(/\{\{|\}\}/g, '');
          parameters.push({
            key: `header_${varNum}`,
            component: 'HEADER',
            varNum,
            placeholder: match,
            label: `Header Variable ${varNum}`,
            description: `Value for ${match} in header`,
          });
        });
      }
    }

    const bodyComponent = components.find(c => c.type === 'BODY');
    const bodyText = bodyComponent?.text || template.body || '';
    const bodyVarMatches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
    bodyVarMatches.forEach((match) => {
      const varNum = match.replace(/\{\{|\}\}/g, '');
      parameters.push({
        key: `body_${varNum}`,
        component: 'BODY',
        varNum,
        placeholder: match,
        label: `Body Variable ${varNum}`,
        description: `Value for ${match} in body text`,
      });
    });

    const buttonsComponent = components.find(c => c.type === 'BUTTONS');
    const buttons = buttonsComponent?.buttons || template.buttons || [];
    buttons.forEach((btn, index) => {
      if (btn.type === 'URL' && btn.url && btn.url.includes('{{')) {
        parameters.push({
          key: `button_text_${index}`,
          component: 'BUTTON',
          index,
          placeholder: 'Button URL variable',
          label: `Button ${index + 1} Text`,
          description: `Text for URL button ${index + 1}`,
        });
      }
    });

    return parameters;
  };

  const generateCSVHeaders = (template) => {
    if (!template) {
      return ["phone_number"];
    }

    const headers = ["phone_number"];

    let templateJson = template.template_json || {};
    if (typeof templateJson === 'string') {
      try {
        templateJson = JSON.parse(templateJson);
      } catch (e) {
        templateJson = {};
      }
    }

    const components = templateJson.components || [];

    const headerComponent = components.find(c => c.type === 'HEADER');
    if (headerComponent) {
      const headerFormat = headerComponent.format?.toUpperCase() || template.headerFormat?.toUpperCase() || 'NONE';
      const headerText = headerComponent.text || template.headerText || '';
      
      if (headerFormat === 'TEXT') {
        const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
        if (headerVarMatches.length > 0) {
          headerVarMatches.forEach((match) => {
            const varNum = match.replace(/\{\{|\}\}/g, '');
            headers.push(`header_${varNum}`);
          });
        }
      }
    } else if (template.headerFormat || template.headerType) {
      const headerFormat = (template.headerFormat || template.headerType).toUpperCase();
      const headerText = template.headerText || '';
      
      if (headerFormat === 'TEXT') {
        const headerVarMatches = headerText.match(/\{\{(\d+)\}\}/g) || [];
        if (headerVarMatches.length > 0) {
          headerVarMatches.forEach((match) => {
            const varNum = match.replace(/\{\{|\}\}/g, '');
            headers.push(`header_${varNum}`);
          });
        }
      }
    }

    const bodyComponent = components.find(c => c.type === 'BODY');
    const bodyText = bodyComponent?.text || template.body || '';
    const bodyVarMatches = bodyText.match(/\{\{(\d+)\}\}/g) || [];
    if (bodyVarMatches.length > 0) {
      bodyVarMatches.forEach((match) => {
        const varNum = match.replace(/\{\{|\}\}/g, '');
        headers.push(`body_${varNum}`);
      });
    }

    const footerComponent = components.find(c => c.type === 'FOOTER');
    const footerText = footerComponent?.text || template.footer || '';
    if (footerText) {
      const hasFooterVars = footerText.match(/\{\{\d+\}\}/g) && footerText.match(/\{\{\d+\}\}/g).length > 0;
      if (hasFooterVars) {
        headers.push('footer');
      }
    }

    const buttonsComponent = components.find(c => c.type === 'BUTTONS');
    const buttons = buttonsComponent?.buttons || template.buttons || [];
    buttons.forEach((btn, index) => {
      if (btn.type === 'URL') {
        if (btn.url && btn.url.includes('{{')) {
          headers.push(`button_text_${index}`);
        }
      }
    });

    return headers;
  };

  const downloadSampleCSV = () => {
    if (!selectedTemplateForPreview || !originalTemplate) {
      alert("Please select at least one template first to download sample CSV");
      return;
    }

    const headers = generateCSVHeaders(originalTemplate);
    
    const sampleRows = [];
    for (let i = 0; i < 2; i++) {
      const row = headers.map((header) => {
        if (header === 'phone_number') {
          return `+1234567890${i}`;
        } else if (header.startsWith('body_')) {
          return `Sample Body Var ${header.split('_')[1]}`;
        } else if (header.startsWith('header_')) {
          return `Sample Header Var ${header.split('_')[1]}`;
        } else if (header === 'footer') {
          return 'Sample Footer';
        } else if (header.startsWith('button_text_')) {
          return 'Click Here';
        }
        return '';
      });
      sampleRows.push(row.join(','));
    }

    const csvContent = [headers.join(','), ...sampleRows].join('\n');
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `campaign_sample_${originalTemplate.name || 'template'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    const newErrors = {};
    if (!campaignData.name.trim()) {
      newErrors.name = "Campaign name is required";
    }
    if (!campaignData.templateIds || campaignData.templateIds.length === 0) {
      newErrors.templateIds = "Please select at least one template";
    }
    if (templateValidationError) {
      newErrors.templateIds = templateValidationError;
    }
    if (!campaignData.whatsapp_account_id) {
      newErrors.whatsapp_account_id = "Please select a WhatsApp account";
    }
    if (useSegment) {
      if (!campaignData.segment_id) {
        newErrors.segment_id = "Please select a contact list";
      }
    } else {
      if (!campaignData.csvFile) {
        newErrors.csvFile = "Please upload a CSV file";
      }
    }
    if (campaignData.sendOption === "schedule") {
      if (!campaignData.scheduledDate || !campaignData.scheduledDate.trim()) {
        newErrors.scheduledDate = "Please select a scheduled date";
      }
      
      if (!campaignData.scheduledTime || !campaignData.scheduledTime.trim()) {
        newErrors.scheduledTime = "Please select a scheduled time";
      }
      
      if (campaignData.scheduledDate && campaignData.scheduledTime) {
        const timeWithSeconds = campaignData.scheduledTime.length === 5 
          ? `${campaignData.scheduledTime}:00` 
          : campaignData.scheduledTime;
        const timestamp = `${campaignData.scheduledDate} ${timeWithSeconds}`;
        const timestampRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;
        
        if (!timestampRegex.test(timestamp)) {
          newErrors.scheduledTimestamp = "Invalid date or time format";
        } else {
          // Validate timezone is selected
          if (!campaignData.timezone || !campaignData.timezone.trim()) {
            newErrors.timezone = "Please select a timezone";
          } else {
            // Parse scheduled date/time in the selected timezone
            const scheduledMoment = moment.tz(
              `${campaignData.scheduledDate} ${campaignData.scheduledTime}`,
              "YYYY-MM-DD HH:mm",
              campaignData.timezone
            );
            
            if (!scheduledMoment.isValid()) {
              newErrors.scheduledTimestamp = "Invalid date or time values";
            } else {
              // Get current time in the selected timezone
              const nowInTimezone = moment.tz(campaignData.timezone);
              
              // Compare in the same timezone
              if (scheduledMoment.isSameOrBefore(nowInTimezone)) {
                newErrors.scheduledTimestamp = "Scheduled time must be in the future";
              }
            }
          }
        }
      } else if (!campaignData.scheduledTimestamp || !campaignData.scheduledTimestamp.trim()) {
        newErrors.scheduledTimestamp = "Please select both date and time";
      }
      
      if (!campaignData.timezone || !campaignData.timezone.trim()) {
        newErrors.timezone = "Please select a timezone";
      }
    }
    setErrors(newErrors);
    setCsvValidationErrors([]);
    
    if (Object.keys(newErrors).length === 0) {
      setIsSubmitting(true);
      try {
        const filteredDefaults = {};
        Object.keys(defaultValues).forEach(key => {
          // Allow spaces, including just " " as valid value
          // Only exclude null, undefined, or empty string (allow spaces like " ", "  ", etc.)
          if (defaultValues[key] !== null && defaultValues[key] !== undefined && defaultValues[key] !== '') {
            filteredDefaults[key] = defaultValues[key];
          }
        });

        const submitData = {
          campaign_name: campaignData.name,
          description: campaignData.description || "",
          template_ids: campaignData.templateIds,
          whatsapp_account_id: campaignData.whatsapp_account_id,
          segment_id: useSegment ? campaignData.segment_id : null,
          use_segment: useSegment,
          csvFile: !useSegment ? campaignData.csvFile : null,
          defaultValues: Object.keys(filteredDefaults).length > 0 ? filteredDefaults : null,
          variables: {},
          sendOption: campaignData.sendOption,
          scheduledTimestamp: campaignData.sendOption === "schedule" 
            ? campaignData.scheduledTimestamp.trim() 
            : null,
          timezone: campaignData.sendOption === "schedule" 
            ? campaignData.timezone 
            : null,
        };
        
        await onSubmit(submitData);
      } catch (error) {
        console.error("Error creating campaign:", error);
        
        const errorResponse = error.response?.data;
        if (errorResponse?.validation?.errors) {
          const validationErrors = errorResponse.validation.errors;
          const summary = errorResponse.validation.summary;
          
          let summaryMessage = errorResponse.error || "CSV validation failed";
          if (summary && typeof summary === 'object') {
            const { totalRows, validRows, invalidRows } = summary;
            summaryMessage = `CSV validation failed: ${invalidRows} of ${totalRows} rows have errors`;
          } else if (typeof summary === 'string') {
            summaryMessage = summary;
          }
          
          setErrors((prev) => ({
            ...prev,
            csvFile: summaryMessage,
          }));
          
          let errorsArray = [];
          if (Array.isArray(validationErrors)) {
            errorsArray = validationErrors.map(err => {
              if (typeof err === 'string') {
                return err;
              } else if (typeof err === 'object') {
                return JSON.stringify(err);
              }
              return String(err);
            });
          } else if (validationErrors) {
            errorsArray = [typeof validationErrors === 'string' ? validationErrors : JSON.stringify(validationErrors)];
          }
          
          setCsvValidationErrors(errorsArray);
        } else {
          const errorMessage = errorResponse?.error || error.message || "Failed to create campaign";
          console.error("Campaign creation error:", errorMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  // Carousel Navigation
  const scrollCarousel = (direction) => {
    if (!carouselRef.current) return;
    const scrollAmount = 200;
    const newScroll = direction === 'left' 
      ? carouselRef.current.scrollLeft - scrollAmount
      : carouselRef.current.scrollLeft + scrollAmount;
    carouselRef.current.scrollTo({ left: newScroll, behavior: 'smooth' });
  };

  const navigatePreview = (direction) => {
    setActivePreviewIndex((prev) => {
      if (direction === 'next') {
        return prev < selectedTemplates.length - 1 ? prev + 1 : 0;
      } else {
        return prev > 0 ? prev - 1 : selectedTemplates.length - 1;
      }
    });
  };

  // Handle sending test message
  const handleSendTest = async () => {
    if (!testPhoneNumber.trim()) {
      setTestResult({ success: false, message: "Please enter a phone number" });
      return;
    }

    if (!selectedTemplates.length || !originalTemplate) {
      setTestResult({ success: false, message: "Please select a template first" });
      return;
    }

    if (!selectedPhoneAccount?.phoneId) {
      setTestResult({ success: false, message: "No WhatsApp account selected" });
      return;
    }

    setIsSendingTest(true);
    setTestResult(null);

    try {
      const token = localStorage.getItem("userAccessToken");
      const user = JSON.parse(localStorage.getItem("user") || "{}");

      // Clean phone number (remove spaces, dashes, etc.)
      let cleanPhone = testPhoneNumber.replace(/[\s\-\(\)]/g, "");
      if (!cleanPhone.startsWith("+") && !cleanPhone.startsWith("91")) {
        cleanPhone = "91" + cleanPhone; // Default to India country code
      }
      cleanPhone = cleanPhone.replace("+", "");

      // Build userVars from defaultValues
      // Allow spaces, including just " " as valid value
      const userVars = {};
      Object.keys(defaultValues).forEach((key) => {
        if (defaultValues[key] !== null && defaultValues[key] !== undefined && defaultValues[key] !== '') {
          userVars[key] = defaultValues[key];
        }
      });

      const testData = {
        userId: user.id,
        phoneId: selectedPhoneAccount.phoneId,
        template: originalTemplate,
        phone_number: cleanPhone,
        userVars: userVars,
      };

      const response = await sendTestTemplate(token, testData);

      if (response.data.success) {
        setTestResult({
          success: true,
          message: `Test message sent successfully to ${cleanPhone}!`,
          messageId: response.data.messageId,
        });
      } else {
        setTestResult({
          success: false,
          message: response.data.message || "Failed to send test message",
        });
      }
    } catch (error) {
      console.error("Error sending test message:", error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          "Failed to send test message";
      setTestResult({ success: false, message: errorMessage });
    } finally {
      setIsSendingTest(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f3f3] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={onCancel}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-xl transition-all border border-gray-200"
              title="Go back"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Create Campaign</h1>
              <p className="text-sm text-gray-600 mt-1">Set up your WhatsApp campaign</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onCancel}
              className="px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-all shadow-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={`px-4 py-2.5 text-sm font-semibold text-gray-900 rounded-xl transition-all flex items-center gap-2 shadow-sm hover:shadow-md border border-gray-300 ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
              style={{ backgroundColor: "#eefa94" }}
            >
              {isSubmitting && <Loader className="w-4 h-4 animate-spin" />}
              {isSubmitting
                ? "Creating..."
                : campaignData.sendOption === "now"
                ? "Create & Send Now"
                : "Create & Schedule"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: Form */}
          <div className="lg:col-span-2 space-y-4 lg:max-h-[calc(100vh-140px)] lg:overflow-y-auto lg:pr-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            {/* Campaign Info */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <h2 className="text-sm font-bold text-gray-900 mb-3">
                Campaign Information
              </h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-2">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    value={campaignData.name}
                    onChange={(e) => handleInputChange("name", e.target.value)}
                    placeholder="e.g., Welcome Campaign"
                    className={`w-full px-4 py-2.5 text-sm border ${
                      errors.name ? "border-red-300" : "border-gray-300"
                    } rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400`}
                  />
                  {errors.name && (
                    <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                      <AlertCircle size={12} />
                      {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    value={campaignData.description}
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Describe your campaign..."
                    rows={2}
                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 resize-none"
                  />
                </div>
              </div>
            </div>

            {/* Template Selection */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-bold text-gray-900">
                  Select Templates * <span className="text-emerald-600 text-xs font-bold">(Multiple)</span>
                </h2>
                {selectedTemplates.length > 1 && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-full text-xs font-bold text-emerald-700">
                    <Sparkles size={12} />
                    Will Shuffle
                  </span>
                )}
              </div>
              
              {!selectedPhoneAccount ? (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                  <p className="text-sm text-yellow-800 font-medium">
                    Please select a WhatsApp Business Account from the header
                  </p>
                </div>
              ) : loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader className="w-6 h-6 animate-spin text-emerald-600" />
                </div>
              ) : (
                <div className="space-y-3">
                  {/* Search Bar */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={templateSearchQuery}
                      onChange={(e) => setTemplateSearchQuery(e.target.value)}
                      placeholder="Search templates..."
                      className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400"
                    />
                    {templateSearchQuery && (
                      <button
                        onClick={() => setTemplateSearchQuery("")}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>

                  {/* Selected Templates Carousel */}
                  {selectedTemplates.length > 0 && (
                    <div className="relative">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-bold text-gray-900">
                          Selected ({selectedTemplates.length})
                        </span>
                        {selectedTemplates.length > 3 && (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => scrollCarousel('left')}
                              className="p-1 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
                            >
                              <ChevronLeft size={14} />
                            </button>
                            <button
                              onClick={() => scrollCarousel('right')}
                              className="p-1 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
                            >
                              <ChevronRight size={14} />
                            </button>
                          </div>
                        )}
                      </div>

                      <div 
                        ref={carouselRef}
                        className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide"
                        style={{
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none'
                        }}
                      >
                        {selectedTemplates.map((templateId, index) => {
                          const template = templates.find(
                            t => String(t.id).trim() === String(templateId).trim()
                          );
                          if (!template) return null;

                          return (
                            <div
                              key={templateId}
                              className="flex-shrink-0 group"
                            >
                              <div className="inline-flex items-center gap-2 pl-3 pr-2 py-2 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-all cursor-pointer"
                                onClick={() => setActivePreviewIndex(index)}
                              >
                                <span className="inline-flex items-center justify-center w-5 h-5 bg-emerald-600 text-white text-xs font-bold rounded-full flex-shrink-0">
                                  {index + 1}
                                </span>
                                <span className="text-sm font-semibold text-gray-900 max-w-[140px] truncate">
                                  {template.name}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleTemplateToggle(templateId);
                                  }}
                                  className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Remove"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <p className="text-xs text-gray-500 mt-2 flex items-center gap-1 font-medium">
                        <Info size={12} />
                        Click chip to preview • {selectedTemplates.length > 1 ? 'Templates will be randomly shuffled' : 'Add more to enable shuffle'}
                      </p>
                    </div>
                  )}

                  {/* Template List */}
                  <div>
                    <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-xl p-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {(() => {
                          const filteredTemplates = templates.filter((template) => {
                            if (!templateSearchQuery.trim()) return true;
                            const query = templateSearchQuery.toLowerCase();
                            return (
                              template.name?.toLowerCase().includes(query) ||
                              template.category?.toLowerCase().includes(query) ||
                              template.language?.toLowerCase().includes(query)
                            );
                          });

                          if (filteredTemplates.length === 0) {
                            return (
                              <p className="col-span-2 text-sm text-gray-500 text-center py-6 font-medium">
                                {templateSearchQuery
                                  ? `No templates found matching "${templateSearchQuery}"`
                                  : "No approved templates found"}
                              </p>
                            );
                          }

                          return filteredTemplates.map((template) => {
                            const isSelected = selectedTemplates.some(
                              id => String(id).trim() === String(template.id).trim()
                            );
                            return (
                              <label
                                key={template.id}
                                className={`flex items-start gap-2 p-3 rounded-xl cursor-pointer border transition-all ${
                                  isSelected
                                    ? "bg-emerald-50 border-emerald-300"
                                    : "hover:bg-gray-50 border-gray-200 hover:border-gray-300"
                                }`}
                              >
                                <div className="mt-0.5 flex-shrink-0">
                                  {isSelected ? (
                                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                                  ) : (
                                    <div className="w-5 h-5 border-2 border-gray-300 rounded-lg" />
                                  )}
                                </div>
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => handleTemplateToggle(template.id)}
                                  className="hidden"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-bold text-gray-900 truncate">
                                    {template.name}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1">
                                    {template.category && (
                                      <span className="text-xs text-gray-600 font-medium">
                                        {template.category}
                                      </span>
                                    )}
                                    {template.language && (
                                      <span className="text-xs text-gray-400 font-medium">
                                        • {template.language.toUpperCase()}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </label>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                  
                  {(errors.templateIds || templateValidationError) && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-red-600 font-medium">
                          {errors.templateIds || templateValidationError}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {selectedTemplates.length > 0 && !templateValidationError && (
                    <div className="flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl p-3 font-medium">
                      <CheckCircle size={14} />
                      <span>
                        <strong>{selectedTemplates.length} template{selectedTemplates.length > 1 ? 's' : ''}</strong> selected and validated
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Default Values Section */}
            {originalTemplate && extractRequiredParameters(originalTemplate).length > 0 && (
              <div className="bg-white rounded-2xl border border-gray-200 p-4">
                <h2 className="text-sm font-bold text-gray-900 mb-3">
                  Default Values (Optional)
                </h2>
                <p className="text-xs text-gray-600 mb-3 font-medium">
                  Set default values for template parameters. These will be used if a CSV row doesn't have a value for that parameter.
                </p>
                <div className="space-y-3">
                  {extractRequiredParameters(originalTemplate).map((param) => (
                    <div key={param.key}>
                      <label className="block text-xs font-bold text-gray-700 mb-2">
                        {param.label}
                        <span className="text-xs text-gray-500 ml-1 font-medium">
                          ({param.placeholder})
                        </span>
                      </label>
                      <input
                        type="text"
                        value={defaultValues[param.key] || ''}
                        onChange={(e) => {
                          setDefaultValues((prev) => ({
                            ...prev,
                            [param.key]: e.target.value,
                          }));
                        }}
                        placeholder={`Default value for ${param.description}`}
                        className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400"
                      />
                      <p className="text-xs text-gray-500 mt-1 font-medium">
                        {param.description}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recipients Selection */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <h2 className="text-sm font-bold text-gray-900 mb-3">
                Recipients *
              </h2>
              
              <div className="flex gap-4 mb-3">
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="radio"
                    checked={!useSegment}
                    onChange={() => {
                      setUseSegment(false);
                      handleInputChange("use_segment", false);
                    }}
                    className="mr-2 w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                  />
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">
                    Upload CSV
                  </span>
                </label>
                <label className="flex items-center cursor-pointer group">
                  <input
                    type="radio"
                    checked={useSegment}
                    onChange={() => {
                      setUseSegment(true);
                      handleInputChange("use_segment", true);
                    }}
                    className="mr-2 w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                  />
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">
                    Use Contact List
                  </span>
                </label>
              </div>

              {useSegment ? (
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-2">
                      Select Contact List *
                    </label>
                    <select
                      value={campaignData.segment_id}
                      onChange={(e) => handleInputChange("segment_id", e.target.value)}
                      className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 font-medium"
                    >
                      <option value="">-- Select Contact List --</option>
                      {segments.map((segment) => (
                        <option key={segment.segment_id} value={segment.segment_id}>
                          {segment.segment_name} ({segment.contact_count || 0} contacts)
                        </option>
                      ))}
                    </select>
                    {errors.segment_id && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <AlertCircle size={12} />
                        {errors.segment_id}
                      </p>
                    )}
                    {segments.length === 0 && (
                      <p className="text-sm text-gray-500 mt-2 font-medium">
                        No contact lists found. Create one from the Contact Lists page.
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <button
                    onClick={downloadSampleCSV}
                    disabled={!selectedTemplateForPreview || !originalTemplate}
                    className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl transition-all border ${
                      selectedTemplateForPreview && originalTemplate
                        ? "text-emerald-700 bg-emerald-50 hover:bg-emerald-100 cursor-pointer border-emerald-200"
                        : "text-gray-400 bg-gray-100 cursor-not-allowed border-gray-200"
                    }`}
                    title={!selectedTemplateForPreview ? "Please select at least one template first" : "Download sample CSV with template variables"}
                  >
                    <Download size={16} />
                    Download Sample CSV
                  </button>
                  {!selectedTemplateForPreview && (
                    <p className="text-xs text-gray-500 font-medium">
                      Select at least one template to download CSV with required variables
                    </p>
                  )}

                  <div>
                    <label className="block">
                      <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-gray-400 transition-colors cursor-pointer bg-gray-50">
                        <Upload className="mx-auto mb-2 text-gray-400" size={32} />
                        <p className="text-sm font-bold text-gray-700 mb-1">
                          {campaignData.csvFile
                            ? campaignData.csvFile.name
                            : "Click to upload CSV"}
                        </p>
                        <p className="text-xs text-gray-500 font-medium">
                          CSV file with phone numbers and variables
                        </p>
                      </div>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleCSVUpload}
                        className="hidden"
                      />
                    </label>
                    {errors.csvFile && (
                      <div className="mt-3">
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-bold text-red-800 mb-1">
                              {errors.csvFile}
                            </p>
                            {Array.isArray(csvValidationErrors) && csvValidationErrors.length > 0 && (
                              <details className="mt-2">
                                <summary className="text-xs text-red-600 cursor-pointer hover:text-red-800 font-semibold">
                                  View detailed errors ({csvValidationErrors.length})
                                </summary>
                                <ul className="mt-2 ml-4 list-disc space-y-1 max-h-48 overflow-y-auto">
                                  {csvValidationErrors.slice(0, 20).map((error, idx) => {
                                    const errorText = typeof error === 'string' 
                                      ? error 
                                      : typeof error === 'object' 
                                      ? JSON.stringify(error, null, 2)
                                      : String(error);
                                    
                                    return (
                                      <li key={idx} className="text-xs text-red-700">
                                        {errorText}
                                      </li>
                                    );
                                  })}
                                  {csvValidationErrors.length > 20 && (
                                    <li className="text-xs text-red-600 italic font-medium">
                                      ... and {csvValidationErrors.length - 20} more errors
                                    </li>
                                  )}
                                </ul>
                              </details>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {campaignData.csvData.length > 0 && (
                    <div className="mt-3">
                      <p className="text-sm font-bold text-gray-700 mb-2">
                        CSV Preview (First 5 rows):
                      </p>
                      <div className="overflow-x-auto border border-gray-200 rounded-xl">
                        <table className="min-w-full text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              {Object.keys(campaignData.csvData[0] || {}).map(
                                (key) => (
                                  <th
                                    key={key}
                                    className="px-3 py-2 text-left font-bold text-gray-700"
                                  >
                                    {key}
                                  </th>
                                )
                              )}
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {campaignData.csvData.map((row, index) => (
                              <tr key={index}>
                                {Object.values(row).map((value, i) => (
                                  <td key={i} className="px-3 py-2 text-gray-900 font-medium">
                                    {value}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Send Options */}
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <h2 className="text-sm font-bold text-gray-900 mb-3">
                Send Options
              </h2>
              <div className="space-y-3">
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="radio"
                      name="sendOption"
                      value="now"
                      checked={campaignData.sendOption === "now"}
                      onChange={(e) => {
                        handleInputChange("sendOption", e.target.value);
                        if (e.target.value === "now") {
                          handleInputChange("scheduledDate", "");
                          handleInputChange("scheduledTime", "");
                          handleInputChange("scheduledTimestamp", "");
                        }
                      }}
                      className="mr-2 w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                    />
                    <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">
                      Send Now
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer group">
                    <input
                      type="radio"
                      name="sendOption"
                      value="schedule"
                      checked={campaignData.sendOption === "schedule"}
                      onChange={(e) => handleInputChange("sendOption", e.target.value)}
                      className="mr-2 w-4 h-4 text-emerald-600 border-gray-300 focus:ring-emerald-500"
                    />
                    <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900">
                      Schedule
                    </span>
                  </label>
                </div>

                {campaignData.sendOption === "schedule" && (
                  <div className="pt-3 border-t border-gray-200 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-2">
                          Scheduled Date *
                        </label>
                        <input
                          type="date"
                          value={campaignData.scheduledDate}
                          onChange={(e) => handleInputChange("scheduledDate", e.target.value)}
                          min={new Date().toISOString().split("T")[0]}
                          className={`w-full px-4 py-2.5 text-sm border ${
                            errors.scheduledTimestamp || errors.scheduledDate
                              ? "border-red-300"
                              : "border-gray-300"
                          } rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 font-medium`}
                        />
                        {(errors.scheduledTimestamp || errors.scheduledDate) && (
                          <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                            <AlertCircle size={12} />
                            {errors.scheduledDate || errors.scheduledTimestamp}
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-2">
                          Scheduled Time *
                        </label>
                        <input
                          type="time"
                          value={campaignData.scheduledTime}
                          onChange={(e) => handleInputChange("scheduledTime", e.target.value)}
                          step="1"
                          className={`w-full px-4 py-2.5 text-sm border ${
                            errors.scheduledTimestamp || errors.scheduledTime
                              ? "border-red-300"
                              : "border-gray-300"
                          } rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 font-medium`}
                        />
                        {(errors.scheduledTimestamp || errors.scheduledTime) && (
                          <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                            <AlertCircle size={12} />
                            {errors.scheduledTime || errors.scheduledTimestamp}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {campaignData.scheduledTimestamp && (
                      <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                        <p className="text-xs text-gray-600 mb-1 font-medium">Formatted timestamp:</p>
                        <p className="text-sm font-mono font-bold text-gray-900">
                          {campaignData.scheduledTimestamp}
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">
                        Timezone *
                      </label>
                      <select
                        value={campaignData.timezone}
                        onChange={(e) => handleInputChange("timezone", e.target.value)}
                        className={`w-full px-4 py-2.5 text-sm border ${
                          errors.timezone
                            ? "border-red-300"
                            : "border-gray-300"
                        } rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400 font-medium`}
                      >
                        <option value="">-- Select Timezone --</option>
                        <option value="America/New_York">America/New_York (EST/EDT)</option>
                        <option value="America/Chicago">America/Chicago (CST/CDT)</option>
                        <option value="America/Denver">America/Denver (MST/MDT)</option>
                        <option value="America/Los_Angeles">America/Los_Angeles (PST/PDT)</option>
                        <option value="America/Toronto">America/Toronto</option>
                        <option value="Europe/London">Europe/London (GMT/BST)</option>
                        <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
                        <option value="Europe/Berlin">Europe/Berlin (CET/CEST)</option>
                        <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                        <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                        <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                        <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                        <option value="Asia/Shanghai">Asia/Shanghai (CST)</option>
                        <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
                      </select>
                      {errors.timezone && (
                        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                          <AlertCircle size={12} />
                          {errors.timezone}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Preview */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-4 space-y-4">
              {selectedTemplates.length > 0 ? (
                <>
                  {/* Preview Navigation */}
                  {selectedTemplates.length > 1 && (
                    <div className="bg-white rounded-2xl border border-gray-200 p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-gray-900">
                          Preview {activePreviewIndex + 1} of {selectedTemplates.length}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => navigatePreview('prev')}
                            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                          >
                            <ChevronLeft size={18} />
                          </button>
                          <button
                            onClick={() => navigatePreview('next')}
                            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
                          >
                            <ChevronRight size={18} />
                          </button>
                        </div>
                      </div>
                      {(() => {
                        const template = templates.find(
                          t => String(t.id).trim() === String(selectedTemplates[activePreviewIndex]).trim()
                        );
                        return template ? (
                          <p className="text-xs text-gray-600 mt-1 truncate font-medium">
                            {template.name}
                          </p>
                        ) : null;
                      })()}
                    </div>
                  )}

                  {/* WhatsApp Preview */}
                  {(() => {
                    const templateId = selectedTemplates[activePreviewIndex];
                    const template = templates.find(
                      t => String(t.id).trim() === String(templateId).trim()
                    );
                    if (!template) return null;
                    
                    const transformedTemplate = transformTemplateForPreview(template);
                    if (!transformedTemplate) return null;

                    return (
                      <WhatsAppPreview
                        templateData={transformedTemplate}
                        defaultValues={defaultValues}
                      />
                    );
                  })()}

                  {/* Test Template Section */}
                  <div className="bg-white rounded-2xl border border-gray-200 p-4">
                    {!showTestSection ? (
                      <button
                        onClick={() => setShowTestSection(true)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl transition-all"
                      >
                        <Send size={16} />
                        Send Test Message
                      </button>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-bold text-gray-900">
                            Test Template
                          </span>
                          <button
                            onClick={() => {
                              setShowTestSection(false);
                              setTestPhoneNumber("");
                              setTestResult(null);
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg transition-colors"
                          >
                            <X size={16} />
                          </button>
                        </div>

                        <div>
                          <label className="block text-xs font-bold text-gray-600 mb-2">
                            Phone Number (with country code)
                          </label>
                          <div className="flex gap-2">
                            <div className="relative flex-1">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                type="text"
                                value={testPhoneNumber}
                                onChange={(e) => setTestPhoneNumber(e.target.value)}
                                placeholder="e.g., 919876543210"
                                className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-400"
                              />
                            </div>
                            <button
                              onClick={handleSendTest}
                              disabled={isSendingTest || !testPhoneNumber.trim()}
                              className={`px-4 py-2.5 text-sm font-semibold text-white rounded-xl flex items-center gap-2 transition-all ${
                                isSendingTest || !testPhoneNumber.trim()
                                  ? "bg-gray-400 cursor-not-allowed"
                                  : "bg-emerald-600 hover:bg-emerald-700"
                              }`}
                            >
                              {isSendingTest ? (
                                <>
                                  <Loader className="w-4 h-4 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Send size={14} />
                                  Test
                                </>
                              )}
                            </button>
                          </div>
                        </div>

                        {testResult && (
                          <div
                            className={`p-3 rounded-xl text-xs ${
                              testResult.success
                                ? "bg-green-50 border border-green-200 text-green-700"
                                : "bg-red-50 border border-red-200 text-red-700"
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              {testResult.success ? (
                                <CheckCircle size={14} className="mt-0.5 flex-shrink-0" />
                              ) : (
                                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                              )}
                              <span className="font-medium">{testResult.message}</span>
                            </div>
                          </div>
                        )}

                        <p className="text-xs text-gray-500 font-medium">
                          Send a test message to verify the template before launching the campaign.
                        </p>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-base font-bold text-gray-900 mb-2">
                    No Template Selected
                  </p>
                  <p className="text-sm text-gray-500 font-medium">
                    Select at least one template to see preview
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
};

export default CreateCampaignForm;
